import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from './components/Header';
import Hero from './components/Hero';
import Gallery from './components/Gallery';
import Latest from './components/Latest';
import Tournament from './components/Tournament';
import Footer from './components/Footer';
import GoToTop from './components/GoToTop';
import Singles from './components/Singles';
import Accessories from './components/Accessories';
import CookieBanner from './components/CookieBanner';
import AddItemPopup from './components/AddItemPopup';
// Family Store Delivery Notice - Commented out for future use
// import RageExpoNotice from './components/RageExpoNotice';
import { useCart } from './shop/core/cart/CartContext';
import CardsGif from './assets/images/cards.gif';
import KamelesanImage from './assets/images/Kamelesan.jpeg';
import FaviconUrl from './assets/images/favicon.ico';

function App() {
  const [isGoTopVisible, setIsGoTopVisible] = useState(false);
  const location = useLocation();
  const { popupOpen, popupMessage, hidePopup } = useCart();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY >= 800) {
        setIsGoTopVisible(true);
      } else {
        setIsGoTopVisible(false);
      }
    };

    window.addEventListener('scroll', () => handleScroll());
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to section when a hash is present (e.g., /#tournament)
  useEffect(() => {
    if (location && location.hash) {
      // Small delay to ensure DOM is ready and avoid conflicts with ScrollToTop
      const timeout = setTimeout(() => {
        const target = document.querySelector(location.hash);
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
      
      return () => clearTimeout(timeout);
    }
  }, [location]);

  return (
    <>
      <Helmet>
        <title>Oracle Gaming - South Africa's #1 Pokemon & One Piece TCG Store | Buy Online</title>
        <meta name="description" content="South Africa's premier Pokemon & One Piece TCG store. Buy Pokemon cards, One Piece cards, Yu-Gi-Oh, Magic: The Gathering online. Free shipping over R1899. Weekly tournaments in Bloemfontein." />
        <meta name="keywords" content="Pokemon cards South Africa, One Piece cards, TCG South Africa, Pokemon TCG online, One Piece TCG, trading cards South Africa, Pokemon booster packs, One Piece booster boxes, Yu-Gi-Oh cards, Magic The Gathering, TCG tournaments Bloemfontein, Pokemon singles, One Piece singles, TCG accessories, card sleeves, deck boxes, South Africa Pokemon cards, buy Pokemon cards online, One Piece cards online" />
        <meta property="og:title" content="Oracle Gaming - South Africa's #1 Pokemon & One Piece TCG Store" />
        <meta property="og:description" content="South Africa's premier Pokemon & One Piece TCG store. Buy Pokemon cards, One Piece cards, Yu-Gi-Oh, Magic: The Gathering online. Free shipping over R1899. Weekly tournaments in Bloemfontein." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://oracle-gaming.vercel.app" />
        <meta property="og:image" content="https://oracle-gaming.vercel.app/images/logo.svg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Oracle Gaming - South Africa's #1 Pokemon & One Piece TCG Store" />
        <meta name="twitter:description" content="South Africa's premier Pokemon & One Piece TCG store. Buy Pokemon cards, One Piece cards, Yu-Gi-Oh, Magic: The Gathering online. Free shipping over R1899." />
        <meta name="twitter:image" content="https://oracle-gaming.vercel.app/images/logo.svg" />
        <link rel="canonical" href="https://oracle-gaming.vercel.app" />
        <link rel="icon" type="image/x-icon" href={FaviconUrl} />
        <link rel="shortcut icon" type="image/x-icon" href={FaviconUrl} />
        <link rel="apple-touch-icon" href={FaviconUrl} />
        <link rel="icon" type="image/png" sizes="32x32" href={FaviconUrl} />
        <link rel="icon" type="image/png" sizes="16x16" href={FaviconUrl} />
        <link rel="manifest" href="/manifest.json" />
      </Helmet>
      <Header />
      <main>
        <article>
          <Hero />
          <Gallery />
          <Latest />
          <div className="section-wrapper tournament-wrapper">
            <Tournament />
          </div>
          <Singles/>
          <div className="section-wrapper">
            <section className="about" id="about">
              <div className="container">
                <figure className="about-banner">
                  <img src={CardsGif} alt="Trading card games at Oracle Gaming" className="about-img" />
                </figure>

                <div className="about-content">
                  <p className="about-subtitle">Your gaming destination</p>
                  <h2 className="about-title">Everything you need for your TCG journey</h2>
                  <p className="about-text">
                    Oracle Gaming is your premier destination for all things trading card games. We stock the latest
                    Pokémon, One Piece, and Yu-Gi-Oh! cards, plus accessories and supplies to enhance your gaming experience.
                    Whether you're building your first deck or competing at the highest level, we have what you need.
                  </p>
                  <p className="about-text">
                    From booster packs and singles to deck boxes and sleeves, we curate the best products to help you
                    succeed. Our knowledgeable team is always ready to help with recommendations, deck building advice,
                    and finding those hard-to-get cards for your collection.
                  </p>
                  <p className="about-bottom-text">
                    <ion-icon name="arrow-forward-circle-outline"></ion-icon>
                    <span>Discover your next favorite card at Oracle Gaming</span>
                  </p>
                </div>
              </div>
            </section>
          </div>
          <Accessories/>
          <div className="section-wrapper">
            <div className="container" style={{textAlign: 'center', paddingBottom: '40px', paddingTop: '60px'}}>
              <h2 className="h2 section-title" style={{marginBottom: '10px'}}>ABOUT US</h2>
            </div>
            <section className="about about-flipped about-us-section" id="owner" style={{paddingTop: '20px'}}>
              <div className="container about-us-container">
                <div className="about-content about-us-content">
                  <p className="about-subtitle">The founder</p>
                  <h2 className="about-title">Building bridges in the gaming community</h2>
                  <p className="about-text">
                    Meet Kamelesan Pillay, the passionate founder behind Oracle Gaming. As a lifelong gamer and collector, Kamelesan 
                    understands the thrill of opening that perfect pack, the strategy behind building the ultimate deck, and the joy 
                    of sharing those moments with fellow enthusiasts.
                  </p>
                  <p className="about-text">
                    With years of experience in competitive play and community building, Kamelesan's vision is simple: create a space 
                    where every player, from casual collectors to tournament champions, feels valued and supported in their gaming journey. 
                    His dedication to fostering a welcoming community has made Oracle Gaming a hub for gamers of all levels.
                  </p>
                  <p className="about-bottom-text">
                    <ion-icon name="arrow-forward-circle-outline"></ion-icon>
                    <span>Join our growing community today</span>
                  </p>
                </div>

                <figure className="about-banner about-us-banner">
                  <img src={KamelesanImage} alt="Kamelesan Pillay, founder of Oracle Gaming" className="about-img about-us-img" />
                </figure>
              </div>
            </section>
          </div>
        </article>
      </main>
      <Footer />
      <GoToTop isVisible={isGoTopVisible} />
      <CookieBanner />
      {/* Family Store Delivery Notice - Commented out for future use */}
      {/* <RageExpoNotice /> */}
      <AddItemPopup isOpen={popupOpen} message={popupMessage} onClose={hidePopup} />
    </>
  );
}

export default App;
